/**
 * displayLegalName: the ONE casing rule for a trust's or a company's legal
 * name, applied at RENDER, never at write (change-register TYP-02: "One
 * casing rule, applied at render, not at write").
 *
 * ── Why there is one ──────────────────────────────────────────────────────
 *
 * Round 11, NEW-TRUST-NAME-RECASE. The grader's trust was printed three ways
 * on four screens: "THE R11 Grader Family Trust" on the review and finish
 * screens, "The R11 Grader Family Trust" on My trusts. There were three
 * helpers doing it: a word-by-word `displayTrustName`, a whole-string
 * `toDisplayCase` on the setup screens, and nothing at all on the rest, which
 * printed whatever the API sent. And the API itself changed: T14 now STORES a
 * legal name exactly as the deed prints it (`backend/src/lib/ingestion/
 * legal-name.ts`, whitespace normalised only), so "THE R11 GRADER FAMILY
 * TRUST" reaches the browser, and making it readable is this file's job alone.
 * `displayTrustName` and `toDisplayCase` are now this function under their old
 * names, so existing call sites cannot drift from it.
 *
 * ── The rule ──────────────────────────────────────────────────────────────
 *
 * 1. A name in ONE case (all capitals, as deeds and CIPC certificates print
 *    them, or all lower case from an extraction) carries no casing decision of
 *    its own, so it gets one deliberate readable form:
 *      - each word capitalised: "THE R11 GRADER FAMILY TRUST" -> "The R11
 *        Grader Family Trust";
 *      - a token with a digit in it is an identifier and is kept exactly as
 *        printed: "R11", "AST-04", "IT1234/2020", "2013/108006/07";
 *      - connectors (the, of, and, for, in, die, en, vir, te) stay lower case
 *        unless they open the name;
 *      - a PARTICLE (van, de, der, den, du, le, la, von, ter, da, dos) is
 *        capitalised when no first name precedes it, the Afrikaans and Dutch
 *        convention (QA 4 Oct, P2-18): it opens the name or follows an article,
 *        a connector or a title ("The Van der Merwe Family Trust", "Du Plessis
 *        Familie Trust", "Dr Van der Merwe"), and is lower case after a first
 *        name or an initial ("Johan du Toit Trust", "The J van der Merwe
 *        Trust"). Only the first word of a run: "Van der Merwe", "De la Rey";
 *      - company legal forms are written the way they are conventionally
 *        written: "(Pty) Ltd", "Inc", "CC", "NPC", "(RF)", "SOC", "(Edms) Bpk";
 *      - a roman numeral II to XII stays a numeral ("Trust II"); a single
 *        letter and dotted initials ("N.O.", "J.P.") stay capitals;
 *      - a title (Mr, Mrs, Ms, Dr, Prof, Adv, Mnr, Mev, Me, Ds) has one capital;
 *      - THE ACRONYM RULE (P2-18): one of six known short forms that carry a
 *        vowel (IT, SA, RSA, BEE, NPO, PBO), or a word of two to five letters
 *        with NO vowel (a, e, i, o, u, y, accents included: BMW, KPMG, FNB,
 *        KZN), stays in capitals. Any other word is a word, so "ABC" shows as
 *        "Abc": in an all-capitals name it cannot be told apart from a short
 *        surname such as NEL or LEE, and keeping every short word in capitals
 *        is the old W5 rule that produced "Johan NEL Family Trust". "NG" is the
 *        one surname excepted from the no-vowel test;
 *      - the letter after a hyphen, an apostrophe or an opening bracket is a
 *        word start ("Smith-Jones", "O'Brien"), except a possessive s
 *        ("Smith's");
 *      - accented letters are letters (P2-18): "ÉTIENNE" gives "Étienne".
 * 2. A name in MIXED case was cased by a person, and is returned byte for
 *    byte. "ABSA Staff Trust" keeps its acronym, "McDonald" keeps its capital
 *    D, "van der Merwe Trust" keeps its lower-case opening particle.
 * 3. ONE exception to 2, and only one, because it is not a person's casing: a
 *    mixed-case name that OPENS with an all-capitals article, "THE" or "DIE",
 *    has that one word written "The" or "Die". This is exactly what the
 *    backend's old recaser stored ("THE R11 Grader Family Trust", Beno's "DIE
 *    Waleno Familie Trust", S26 of 15 July) before T14, and those rows are in
 *    the database. Without it an old row and a re-ingested copy of the same
 *    deed would render differently, which is the finding.
 *
 * Person names are NOT this function's business: `displayPersonName` in
 * `./person-name.ts` owns those, and its rules differ (no acronym rule).
 *
 * ── The contract with the backend (QA 4 Oct, P2-17) ───────────────────────
 *
 * The backend writes trust and company names into email and notification
 * text, so it carries this same rule (`backend/src/lib/format/legal-name.ts`).
 * The code below is a line-for-line port of that file, and the two are held to
 * ONE vector table, `shared/name-vectors/legal-name.json` (this side's test:
 * `__tests__/legal-name.test.ts`); `scripts/check-name-parity.ts` runs
 * every row and a generated corpus through BOTH and fails on any difference.
 * Change the rule only by changing the table first, then both functions.
 *
 * Display only. Callers never write the result back, and anything that must
 * match the stored string exactly (the "type the trust's exact name to
 * delete it" confirmation) keeps using the stored value.
 */

/** Lower case unless they open the name or (particles only) follow an article,
 *  connector or title. */
const CONNECTORS: ReadonlySet<string> = new Set([
  'the', 'of', 'and', 'for', 'in', 'die', 'en', 'vir', 'te',
]);

/** Name particles: capitalised when no first name precedes them (P2-18). */
const PARTICLES: ReadonlySet<string> = new Set([
  'van', 'de', 'der', 'den', 'du', 'le', 'la', 'von', 'ter', 'da', 'dos',
]);

/** Titles. Written with one capital, and not a first name for the particle
 *  rule. Checked before the no-vowel acronym test (DR, MR, MNR have no vowel). */
const TITLES: ReadonlySet<string> = new Set([
  'mr', 'mrs', 'ms', 'miss', 'dr', 'prof', 'adv', 'mnr', 'mev', 'me', 'mej', 'ds',
]);

/** Company legal forms, by their letters alone, to how they are written. */
const LEGAL_FORMS: Readonly<Record<string, string>> = {
  PTY: 'Pty',
  LTD: 'Ltd',
  INC: 'Inc',
  CC: 'CC',
  NPC: 'NPC',
  RF: 'RF',
  SOC: 'SOC',
  BK: 'BK',
  EDMS: 'Edms',
  BPK: 'Bpk',
};

/** Short forms that carry a vowel and are kept in capitals anyway. IT is the
 *  Master's reference prefix ("(IT 3213/2000)"). Deliberately short: every entry
 *  is a word no South African surname or trust word spells. */
const KNOWN_ACRONYMS: ReadonlySet<string> = new Set(['IT', 'SA', 'RSA', 'BEE', 'NPO', 'PBO']);

/** No-vowel words that are names, not acronyms. */
const NOT_ACRONYMS: ReadonlySet<string> = new Set(['NG']);

/** Only the numerals a name realistically carries. A bare "I" is excluded: it is
 *  far likelier to be an initial, and an initial is also upper case. */
const ROMAN_NUMERAL = /^(II|III|IV|VI|VII|VIII|IX|XI|XII)$/;

/** Dotted initials: "N.O.", "J.P.", "J.P". */
const DOTTED_INITIALS = /^(?:\p{L}\.)+\p{L}?$/u;

/** Opening articles the old backend recaser left in capitals (rule 3). */
const LEGACY_LEADING_ARTICLES: ReadonlySet<string> = new Set(['THE', 'DIE']);

type WordRole = 'connector' | 'title' | 'particle' | 'word';

/** The letters of a token, accents stripped, upper case: what the lists match. */
function baseLetters(token: string): string {
  return token
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}]/gu, '')
    .toUpperCase();
}

/** Upper-case, except a letter whose capital is not one letter (the German
 *  sharp s) is left as it is, so the rule stays idempotent. */
function upperCase(s: string): string {
  return Array.from(s, (c) => {
    const u = c.toUpperCase();
    return u.length === c.length ? u : c;
  }).join('');
}

function capitaliseRuns(lower: string): string {
  // Capitalise the first letter of each run of letters, where a run starts the
  // token or follows a hyphen, apostrophe, opening bracket or slash. A lone "s"
  // after an apostrophe is a possessive and stays lower case.
  return lower.replace(/(^|[-'’(/])(\p{L}[\p{L}\p{M}]*)/gu, (_m, sep: string, run: string) => {
    if ((sep === "'" || sep === '’') && run === 's') return sep + run;
    return sep + upperCase(run.charAt(0)) + run.slice(1);
  });
}

/** Case one word of a one-case name, given what the word before it was. */
function caseWord(token: string, isFirst: boolean, prevRole: WordRole | null): { out: string; role: WordRole } {
  // An identifier: keep it exactly as printed.
  if (/\p{N}/u.test(token)) return { out: token, role: 'word' };

  const letters = baseLetters(token);
  // "&", "-", "/": joins two names, so the next particle starts a new one.
  if (letters.length === 0) return { out: token, role: 'connector' };

  const form = LEGAL_FORMS[letters];
  if (form) return { out: token.replace(/\p{L}+/u, form), role: 'word' };
  if (ROMAN_NUMERAL.test(letters)) return { out: upperCase(token), role: 'word' };
  // A single letter is an initial; dotted letters are initials too ("N.O.").
  if (letters.length === 1) return { out: upperCase(token), role: 'word' };
  const core = token.replace(/^[("'‘“]+/, '').replace(/[)"'’”,;:]+$/, '');
  if (DOTTED_INITIALS.test(core)) return { out: upperCase(token), role: 'word' };

  const lower = token.toLowerCase();
  const bare = lower.replace(/\.$/, '');
  if (TITLES.has(bare)) return { out: capitaliseRuns(lower), role: 'title' };
  if (CONNECTORS.has(lower)) return { out: isFirst ? capitaliseRuns(lower) : lower, role: 'connector' };
  if (PARTICLES.has(lower)) {
    // No first name before it: it opens the name, or the word before it was an
    // article, a connector or a title. A particle after a particle ("der" in
    // "Van der Merwe") is the run's tail and stays lower case.
    const noFirstName = isFirst || prevRole === 'connector' || prevRole === 'title';
    return { out: noFirstName ? capitaliseRuns(lower) : lower, role: 'particle' };
  }

  if (KNOWN_ACRONYMS.has(letters)) return { out: upperCase(token), role: 'word' };
  if (
    letters.length >= 2 &&
    letters.length <= 5 &&
    !/[AEIOUY]/.test(letters) &&
    !NOT_ACRONYMS.has(letters)
  ) {
    return { out: upperCase(token), role: 'word' };
  }
  return { out: capitaliseRuns(lower), role: 'word' };
}

/** Recase a single-case name, preserving its whitespace exactly. */
function recase(raw: string): string {
  let prevRole: WordRole | null = null;
  return raw
    .split(/(\s+)/)
    .map((part) => {
      if (part === '' || /^\s+$/.test(part)) return part;
      const { out, role } = caseWord(part, prevRole === null, prevRole);
      prevRole = role;
      return out;
    })
    .join('');
}

/** Rule 3: an all-caps "THE" / "DIE" opening an otherwise mixed-case name. */
function repairLegacyLeadingArticle(raw: string): string {
  const match = /^(\s*)(\S+)/.exec(raw);
  if (!match) return raw;
  const lead = match[1] ?? '';
  const first = match[2];
  if (!first || !LEGACY_LEADING_ARTICLES.has(first)) return raw;
  const rest = raw.slice(lead.length + first.length);
  // Only when something after it carries lower case: an all-caps name never
  // reaches this function, and "THE" alone is not a name.
  if (!/\p{Ll}/u.test(rest)) return raw;
  return lead + first.charAt(0) + first.slice(1).toLowerCase() + rest;
}

/**
 * Show a trust's or company's legal name in the one readable form. Never throws,
 * never mutates; null and undefined give ''. Idempotent: its own output is mixed
 * case and comes back unchanged.
 */
export function displayLegalName(name: string | null | undefined): string {
  if (name === null || name === undefined) return '';
  const raw = String(name);
  if (raw.trim() === '') return raw;

  const hasUpper = /\p{Lu}/u.test(raw);
  const hasLower = /\p{Ll}/u.test(raw);
  if (hasUpper && hasLower) return repairLegacyLeadingArticle(raw);
  // No cased letters at all (digits only, another script): nothing to say.
  if (!hasUpper && !hasLower) return raw;
  return recase(raw);
}
